// src/services/system-admin/transport-bookings.service.ts
import { API_BASE_URL } from "@/lib/api";
import type {
PagedResult,
  TransportBookingListItem,
  ApiTransportBookingListItem,
  TransportBookingDetail,
  TransportBookingDetailResponse,
  TransportBookingHistoryItem,
  GetTransportBookingsParams,
  ConfirmBookingRequest,
  RejectBookingRequest,
  RequestBookingInfoRequest,
  AssignVehicleRequest,
  AssignPartnerRequest,
  CancelBookingRequest,
  CommentOnlyRequest,
  CreateQuoteRequest,
} from "@/types/admin/transport-booking-type";

function authHeaders(): HeadersInit {
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("adminAccessToken") || ""
      : "";
  return {
    Accept: "application/json, text/plain, */*",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Maps the API's raw field names onto the shape the bookings table expects.
function mapBookingListItem(
  raw: ApiTransportBookingListItem,
): TransportBookingListItem {
  return {
    bookingUid: raw.uid,
    bookingReference: raw.bookingReference,
    status: raw.status,
    paymentStatus: raw.paymentStatus,
    partnerId: raw.partnerId,
    vehicleUid: raw.vehicleUid,
    registrationNumber: raw.vehicleUid ? raw.vehicleUid.slice(0, 8) : null,
    customerName: raw.contactName,
    pickupLocation: raw.pickupAddress,
    pickupAt: raw.pickupDatetime,
    totalAmount:
      raw.confirmedAmount ?? raw.quotedAmount ?? raw.estimatedAmount ?? null,
    currency: raw.currency,
    createdAt: raw.creationDate,
  };
}

async function handle<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    let message = fallback;
    try {
      const errData = await res.json();
      message = errData?.title || errData?.message || errData?.error || fallback;
    } catch {
      // response wasn't JSON — keep fallback
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export const transportBookingsService = {
  /** GET /api/admin/transport/bookings */
  async getBookings(
    params: GetTransportBookingsParams,
  ): Promise<PagedResult<TransportBookingListItem>> {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.paymentStatus) query.set("paymentStatus", params.paymentStatus);
    if (params.search) query.set("search", params.search);
    if (params.partnerId) query.set("partnerId", String(params.partnerId));
    if (params.vehicleUid) query.set("vehicleUid", params.vehicleUid);
    if (params.pickupFrom) query.set("pickupFrom", params.pickupFrom);
    if (params.pickupTo) query.set("pickupTo", params.pickupTo);
    query.set("page", String(params.page ?? 1));
    query.set("pageSize", String(params.pageSize ?? 20));

  const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings?${query.toString()}`,
      { method: "GET", headers: authHeaders() },
    );
    const raw = await handle<PagedResult<ApiTransportBookingListItem>>(
      res,
      "Failed to load bookings.",
    );
    return { ...raw, items: raw.items.map(mapBookingListItem) };
  },

  /** GET /api/admin/transport/bookings/{bookingUid} */
 async getBookingById(
    bookingUid: string,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}`,
      { method: "GET", headers: authHeaders() },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to load booking details.",
    );
  },

  /** GET /api/admin/transport/bookings/{bookingUid}/history */
  async getBookingHistory(
    bookingUid: string,
  ): Promise<TransportBookingHistoryItem[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/history`,
      { method: "GET", headers: authHeaders() },
    );
    return handle<TransportBookingHistoryItem[]>(
      res,
      "Failed to load booking history.",
    );
  },

  /** PATCH /api/admin/transport/bookings/{bookingUid}/confirm */
async confirmBooking(
    bookingUid: string,
    payload: ConfirmBookingRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/confirm`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to confirm booking.",
    );
  },

  /** POST /api/admin/transport/bookings/{bookingUid}/quotes */
async createQuote(
    bookingUid: string,
    payload: CreateQuoteRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/quotes`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to create quote.",
    );
  },

  /** PATCH /api/admin/transport/bookings/{bookingUid}/reject */
async rejectBooking(
    bookingUid: string,
    payload: RejectBookingRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/reject`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to reject booking.",
    );
  },

  /** PATCH /api/admin/transport/bookings/{bookingUid}/request-information */
 async requestInformation(
    bookingUid: string,
    payload: RequestBookingInfoRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/request-information`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to request information.",
    );
  },

  /** PATCH /api/admin/transport/bookings/{bookingUid}/assign-vehicle */
async assignVehicle(
    bookingUid: string,
    payload: AssignVehicleRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/assign-vehicle`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to assign vehicle.",
    );
  },

  /** PATCH /api/admin/transport/bookings/{bookingUid}/assign-partner */
async assignPartner(
    bookingUid: string,
    payload: AssignPartnerRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/assign-partner`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to assign partner.",
    );
  },

  /** PATCH /api/admin/transport/bookings/{bookingUid}/cancel */
 async cancelBooking(
    bookingUid: string,
    payload: CancelBookingRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/cancel`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to cancel booking.",
    );
  },

  /** PATCH /api/admin/transport/bookings/{bookingUid}/start */
  async startBooking(
    bookingUid: string,
    payload: CommentOnlyRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/start`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to start booking.",
    );
  },

  /** PATCH /api/admin/transport/bookings/{bookingUid}/complete */
async completeBooking(
    bookingUid: string,
    payload: CommentOnlyRequest,
  ): Promise<TransportBookingDetailResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/complete`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<TransportBookingDetailResponse>(
      res,
      "Failed to complete booking.",
    );
  },
};